import { Outlet, NavLink, useNavigate, Link } from 'react-router-dom';
import { useEffect, useState, useCallback } from 'react';
import { LogOut, LayoutDashboard, Inbox, FileText, Pencil, Image, ImagePlus, Search, BarChart3, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import BrandMark from '../../components/brand/BrandMark';
import { adminLogout, getAdminEmail, verifyAdminSession } from '../../lib/auth';

const NAV = [
    { to: '/admin/dashboard', icon: LayoutDashboard, label: 'Overview' },
    { to: '/admin/leads',     icon: Inbox,           label: 'Leads' },
    { to: '/admin/blog',      icon: FileText,        label: 'Blog' },
    { to: '/admin/content',   icon: Pencil,          label: 'Content' },
    { to: '/admin/gallery',   icon: Image,           label: 'Gallery' },
    { to: '/admin/brand-images', icon: ImagePlus,    label: 'Brand Images' },
    { to: '/admin/seo',       icon: Search,          label: 'SEO' },
    { to: '/admin/analytics', icon: BarChart3,       label: 'Analytics' },
];

export default function AdminLayout() {
    const navigate = useNavigate();
    const [adminEmail, setAdminEmailState] = useState(getAdminEmail());
    const [checking, setChecking] = useState(true);

    // Attach noindex meta and lock page title while inside the admin console
    useEffect(() => {
        const meta = document.createElement('meta');
        meta.name = 'robots';
        meta.content = 'noindex, nofollow, noarchive';
        document.head.appendChild(meta);
        const origTitle = document.title;
        document.title = 'Admin Console · DigiTech Cloud AI';
        return () => {
            try { document.head.removeChild(meta); } catch (err) {
                // Node may have already been removed by a concurrent unmount — safe to ignore
                if (process.env.NODE_ENV !== 'production') console.debug('[AdminLayout] meta cleanup:', err);
            }
            document.title = origTitle;
        };
    }, []);

    // Verify the httpOnly admin session cookie is still valid on mount.
    // If not, kick the user back to /admin (login).
    useEffect(() => {
        let cancelled = false;
        (async () => {
            const res = await verifyAdminSession();
            if (cancelled) return;
            if (!res.ok) {
                if (process.env.NODE_ENV !== 'production') console.debug('[AdminLayout] Auth verification failed:', res.error);
                navigate('/admin', { replace: true });
                return;
            }
            if (res.email) setAdminEmailState(res.email);
            setChecking(false);
        })();
        return () => { cancelled = true; };
    }, [navigate]);

    const logout = useCallback(async () => {
        await adminLogout();
        navigate('/admin');
    }, [navigate]);

    if (checking) {
        return (
            <div className="dt-space-bg min-h-screen flex items-center justify-center text-muted-foreground text-sm">
                Verifying session…
            </div>
        );
    }

    return (
        <div className="dt-space-bg min-h-screen flex" data-testid="admin-layout">
            <div className="absolute inset-0 dt-grid-overlay dt-noise pointer-events-none" />
            <aside className="hidden md:flex md:w-64 lg:w-72 shrink-0 flex-col border-r border-white/10 bg-[rgba(8,12,20,0.85)] sticky top-0 h-screen z-30">
                <div className="px-5 py-5 border-b border-white/10">
                    <Link to="/" className="inline-flex items-center gap-2">
                        <BrandMark className="h-7 w-7" />
                        <span className="font-display text-base font-semibold">DigiTech <span className="text-primary">Admin</span></span>
                    </Link>
                    <div className="mt-1 text-[11px] font-mono uppercase tracking-widest text-muted-foreground">{adminEmail}</div>
                </div>
                <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
                    {NAV.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            data-testid={`admin-nav-${n.label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `flex items-center gap-2 px-3 py-2.5 rounded-md text-sm transition-colors ${
                                    isActive
                                        ? 'bg-primary/10 text-primary border border-primary/30'
                                        : 'text-foreground/80 hover:bg-white/5 hover:text-foreground border border-transparent'
                                }`
                            }
                        >
                            <n.icon className="h-4 w-4" />
                            {n.label}
                        </NavLink>
                    ))}
                </nav>
                <div className="p-3 border-t border-white/10">
                    <Link to="/" className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-foreground/80 hover:bg-white/5">
                        <Sparkles className="h-4 w-4 text-primary" /> View site
                    </Link>
                    <Button onClick={logout} variant="ghost" size="sm" data-testid="admin-logout-button" className="w-full justify-start mt-1 text-foreground/80">
                        <LogOut className="h-4 w-4 mr-1.5" /> Sign out
                    </Button>
                </div>
            </aside>

            {/* Mobile top bar */}
            <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-background/80 backdrop-blur-xl border-b border-white/10">
                <div className="px-4 h-14 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2"><BrandMark className="h-6 w-6" /><span className="font-display font-semibold">Admin</span></Link>
                    <Button onClick={logout} variant="ghost" size="sm"><LogOut className="h-4 w-4 mr-1" />Logout</Button>
                </div>
                <div className="flex overflow-x-auto px-2 pb-2 gap-1 no-scrollbar">
                    {NAV.map((n) => (
                        <NavLink key={n.to} to={n.to} className={({ isActive }) => `inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs whitespace-nowrap ${isActive ? 'bg-primary/10 text-primary border border-primary/30' : 'border border-white/10 text-foreground/70'}`}>
                            <n.icon className="h-3.5 w-3.5" /> {n.label}
                        </NavLink>
                    ))}
                </div>
            </div>

            <main className="flex-1 min-w-0 relative md:pt-0 pt-32">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-10">
                    <Outlet />
                </div>
            </main>
        </div>
    );
}
